import { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send, Loader2, Instagram, ArrowUpRight, MessageCircle, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    toast.success('Message sent! We will get back to you soon.');
    setFormData({ name: '', email: '', phone: '', message: '' });
    setSending(false);
  };

  const stores = [
    {
      name: 'Bhuj Store',
      address: 'Ground Floor, Alfa Arcade, Shop No. 005, Sumra Deli Road, Sonivad, Bhuj, Gujarat - 370001',
      phones: ['+91 93285 66392', '+91 81419 80608'],
      instagram: 'https://www.instagram.com/jat__fashion_house?igsh=MTNudHNvdjJyaHpxcg==',
      instagramHandle: '@jat__fashion_house',
      maps: 'https://share.google/32ArVO6ALVxQlloIC'
    },
    {
      name: 'Anjar Store',
      address: 'Near Guddi Hotel, Devaliya Naka, Anjar, Gujarat - 370110',
      phones: ['+91 84690 82367', '+91 93285 66392'],
      instagram: 'https://www.instagram.com/jat_fashion_house_anjar?igsh=anNlcmgzNm8xeGxp',
      instagramHandle: '@jat_fashion_house_anjar',
      maps: 'https://share.google/2mS7TkcySxIfLGGS6'
    }
  ];

  return (
    <div className="min-h-screen bg-ivory pt-24" data-testid="contact-page">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-charcoal text-white">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <span className="text-gold text-xs tracking-[0.2em] uppercase mb-4 block">Get in Touch</span>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light text-white max-w-2xl">
            Visit Our Stores
          </h1>
          <p className="text-lg text-white/70 max-w-xl mt-6">
            Experience premium men's fashion at our stores in Bhuj and Anjar.
          </p>
          
          {/* Quick Contact Buttons */}
          <div className="flex flex-wrap gap-4 mt-8">
            <a 
              href="https://wa.me/919328566392?text=Hi%20JAT%20Fashion%20House%2C%20I%20have%20a%20query"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#25D366] text-white px-6 py-3 text-xs tracking-wider uppercase font-medium hover:bg-[#128C7E] transition-colors rounded-full"
            >
              <MessageCircle className="w-4 h-4" />
              WhatsApp Us
            </a>
            <a 
              href="tel:+919328566392"
              className="inline-flex items-center gap-2 bg-white/10 text-white px-6 py-3 text-xs tracking-wider uppercase font-medium hover:bg-white/20 transition-colors rounded-full border border-white/20"
            >
              <Phone className="w-4 h-4" />
              Call Now
            </a>
          </div>
        </div>
      </section>

      {/* Store Locations */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {stores.map((store, index) => (
              <div 
                key={index}
                className="bg-white border border-charcoal/10 p-8 md:p-10 hover:shadow-xl transition-shadow"
                data-testid={`store-card-${index}`}
              >
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <span className="text-gold text-xs tracking-[0.15em] uppercase">Store Location</span>
                    <h2 className="font-heading text-2xl md:text-3xl font-light text-charcoal mt-1">
                      {store.name}
                    </h2>
                  </div>
                  <a 
                    href={store.maps}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 bg-charcoal text-white px-4 py-2 text-xs tracking-wider uppercase font-medium hover:bg-charcoal/90 transition-colors"
                  >
                    <MapPin className="w-4 h-4" />
                    View Map
                  </a>
                </div>
                
                {/* Address */}
                <div className="flex gap-4 mb-6 pb-6 border-b border-charcoal/10">
                  <MapPin className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                  <p className="text-charcoal-light leading-relaxed">{store.address}</p>
                </div>
                
                {/* Contact Numbers */}
                <div className="space-y-3 mb-6">
                  <h3 className="text-xs tracking-[0.1em] uppercase text-charcoal-light mb-3">Contact Numbers</h3>
                  {store.phones.map((phone, idx) => (
                    <a 
                      key={idx}
                      href={`tel:${phone.replace(/\s/g, '')}`}
                      className="flex items-center gap-3 text-charcoal hover:text-gold transition-colors group"
                    >
                      <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center group-hover:bg-gold/20 transition-colors">
                        <Phone className="w-4 h-4 text-gold" strokeWidth={1.5} />
                      </div>
                      <span className="font-medium">{phone}</span>
                    </a>
                  ))}
                </div>
                
                {/* Instagram */}
                <a 
                  href={store.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-charcoal hover:text-gold transition-colors group"
                >
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                    <Instagram className="w-4 h-4 text-white" strokeWidth={1.5} />
                  </div>
                  <div>
                    <span className="font-medium block">{store.instagramHandle}</span>
                    <span className="text-xs text-charcoal-light">Follow on Instagram</span>
                  </div>
                  <ArrowUpRight className="w-4 h-4 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Email & Store Hours */}
      <section className="py-16 bg-beige">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Email */}
            <div className="text-center md:text-left">
              <div className="w-14 h-14 rounded-full bg-gold/10 flex items-center justify-center mx-auto md:mx-0 mb-4">
                <Mail className="w-6 h-6 text-gold" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-xl font-light text-charcoal mb-2">Email Us</h3>
              <a 
                href="mailto:jatfashionhouse11@gmail.com"
                className="text-charcoal-light hover:text-gold transition-colors"
              >
                jatfashionhouse11@gmail.com
              </a>
            </div>
            
            {/* Store Hours */}
            <div className="text-center md:text-left">
              <div className="w-14 h-14 rounded-full bg-gold/10 flex items-center justify-center mx-auto md:mx-0 mb-4">
                <Clock className="w-6 h-6 text-gold" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-xl font-light text-charcoal mb-2">Store Hours</h3>
              <p className="text-charcoal-light">Mon - Sat: 10:00 AM - 9:00 PM</p>
              <p className="text-charcoal-light">Sunday: 11:00 AM - 8:00 PM</p>
            </div>
            
            {/* WhatsApp */}
            <div className="text-center md:text-left">
              <div className="w-14 h-14 rounded-full bg-[#25D366]/10 flex items-center justify-center mx-auto md:mx-0 mb-4">
                <MessageCircle className="w-6 h-6 text-[#25D366]" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-xl font-light text-charcoal mb-2">WhatsApp</h3>
              <a 
                href="https://wa.me/919328566392"
                target="_blank"
                rel="noopener noreferrer"
                className="text-charcoal-light hover:text-[#25D366] transition-colors"
              >
                +91 93285 66392
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-16 md:py-24">
        <div className="max-w-[800px] mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="text-gold text-xs tracking-[0.2em] uppercase mb-4 block">Send a Message</span>
            <h2 className="font-heading text-3xl md:text-4xl font-light text-charcoal">
              Have a Question?
            </h2>
            <p className="text-charcoal-light mt-4">
              Fill out the form below and we'll get back to you as soon as possible.
            </p>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6 bg-white p-8 md:p-10 border border-charcoal/10">
            <div>
              <label className="block text-xs tracking-[0.1em] uppercase text-charcoal-light mb-3">
                Your Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder="Enter your name"
                className="w-full px-4 py-3 border border-charcoal/20 bg-transparent focus:outline-none focus:border-gold text-charcoal placeholder:text-charcoal-light/50 transition-colors"
                data-testid="contact-name"
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-xs tracking-[0.1em] uppercase text-charcoal-light mb-3">
                  Email Address *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  placeholder="Enter your email"
                  className="w-full px-4 py-3 border border-charcoal/20 bg-transparent focus:outline-none focus:border-gold text-charcoal placeholder:text-charcoal-light/50 transition-colors"
                  data-testid="contact-email"
                />
              </div>
              <div>
                <label className="block text-xs tracking-[0.1em] uppercase text-charcoal-light mb-3">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="Enter your phone"
                  className="w-full px-4 py-3 border border-charcoal/20 bg-transparent focus:outline-none focus:border-gold text-charcoal placeholder:text-charcoal-light/50 transition-colors"
                  data-testid="contact-phone"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-xs tracking-[0.1em] uppercase text-charcoal-light mb-3">
                Your Message *
              </label>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                required
                rows={5}
                placeholder="How can we help you?"
                className="w-full px-4 py-3 border border-charcoal/20 bg-transparent focus:outline-none focus:border-gold text-charcoal placeholder:text-charcoal-light/50 transition-colors resize-none"
                data-testid="contact-message"
              />
            </div>
            
            <button
              type="submit"
              disabled={sending}
              className="w-full inline-flex items-center justify-center gap-3 bg-charcoal text-ivory px-10 py-4 text-xs tracking-[0.15em] uppercase font-medium hover:bg-charcoal/90 transition-colors disabled:opacity-50"
              data-testid="contact-submit"
            >
              {sending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Send Message
                </>
              )}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Contact;
